import { useState } from 'react';
import { api, apiError } from '@/lib/api';
import { toast } from 'sonner';
import { HugeiconsIcon } from '@hugeicons/react';
import {
  FileAttachmentIcon,
  Download01Icon,
  Add01Icon,
  CloudUploadIcon,
  Cancel01Icon,
  Attachment01Icon,
} from '@hugeicons/core-free-icons';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DOC_TYPES } from '@/lib/constants';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';

import { evidenceDocumentIds } from './documentLinks';
export { evidenceDocumentIds } from './documentLinks';

export async function downloadEvidence(doc) {
  try {
    const { data } = await api.post(`/documents/${doc.id}/download-url`);
    window.open(data.download_url, '_blank', 'noopener,noreferrer');
  } catch (error) {
    toast.error(apiError(error));
  }
}

function formatSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ControlDocuments muestra en la celda de la tabla un chip compacto con el
// número de documentos vinculados. Toda la gestión (ver, descargar, vincular
// documentos ya existentes y adjuntar archivos nuevos) ocurre en un modal, para
// que cada fila de control ocupe una altura mínima y no obligue a hacer scroll
// cuando hay muchos documentos.
// variant:
//   'chip' (default) → chip compacto en la celda que abre el modal en modo "view".
//   'attach-button'  → botón de ícono compacto (junto a acciones) que abre el
//                      modal directamente en modo "attach" para subir archivos.
export function ControlDocuments({ control, documents, canEdit, onSaved, variant = 'chip' }) {
  const [open, setOpen] = useState(false);
  // mode: 'view' | 'link' | 'attach'
  const [mode, setMode] = useState('view');
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);

  // Estado del formulario de adjuntar archivos nuevos
  const [attachTitle, setAttachTitle] = useState('');
  const [attachVersion, setAttachVersion] = useState('1.0');
  const [attachType, setAttachType] = useState(DOC_TYPES[0]);
  const [attachFiles, setAttachFiles] = useState([]);
  const [progress, setProgress] = useState(0);

  const ids = evidenceDocumentIds(control.evidence);
  const available = documents.filter((d) => d.status !== 'Eliminado');
  const linked = available.filter((d) => ids.includes(d.id));

  const resetAttach = () => {
    setAttachTitle('');
    setAttachVersion('1.0');
    setAttachType(DOC_TYPES[0]);
    setAttachFiles([]);
    setProgress(0);
  };

  const openModal = (initialMode = 'view') => {
    setSelected(linked.map((d) => d.id));
    resetAttach();
    setMode(initialMode);
    setOpen(true);
  };

  // Guarda los vínculos seleccionados (documentos existentes) en el control.
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/initiative-controls/${control.id}/documents`, { document_ids: selected });
      toast.success('Documentos vinculados al control');
      setMode('view');
      await onSaved();
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setSaving(false);
    }
  };

  const handleAttachFiles = (e) => {
    const chosen = Array.from(e.target.files || []);
    if (chosen.length > 10) {
      toast.error('Máximo 10 archivos por subida');
      return;
    }
    setAttachFiles(chosen);
  };

  const removeAttachFile = (idx) => {
    setAttachFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  // Sube archivos nuevos como evidencia de la iniciativa y los vincula al control.
  // Reutiliza el flujo de 3 pasos (presigned URLs → PUT a S3 → registro de metadata)
  // y luego fusiona los nuevos IDs con los ya vinculados vía el endpoint existente.
  const attach = async () => {
    if (!attachTitle.trim() || !attachVersion.trim()) {
      toast.error('Indique título y versión del documento');
      return;
    }
    if (attachFiles.length === 0) {
      toast.error('Seleccione al menos un archivo');
      return;
    }

    setSaving(true);
    setProgress(0);
    try {
      // 1. Solicitar presigned URLs para todos los archivos en un solo lote
      const filesMeta = attachFiles.map((f) => ({
        file_name: f.name,
        content_type: f.type || 'application/octet-stream',
        file_size: f.size,
      }));
      const { data: urlData } = await api.post(`/initiatives/${control.initiative_id}/upload-urls`, {
        doc_type: attachType,
        files: filesMeta,
      });
      const urls = urlData.urls;

      // 2. Subir cada archivo directamente a S3 con las presigned PUT URLs
      let completed = 0;
      const uploaded = await Promise.all(
        urls.map((urlInfo, idx) => {
          const file = attachFiles[idx];
          return fetch(urlInfo.upload_url, {
            method: 'PUT',
            headers: { 'Content-Type': file.type || 'application/octet-stream' },
            body: file,
          }).then((res) => {
            if (!res.ok) throw new Error(`Error al subir ${file.name}`);
            completed++;
            setProgress(Math.round((completed / urls.length) * 100));
            return urlInfo;
          });
        }),
      );

      // 3. Registrar la metadata de cada documento en la iniciativa
      const registered = await Promise.all(
        uploaded.map((urlInfo, idx) =>
          api.post(`/initiatives/${control.initiative_id}/documents`, {
            title:
              attachFiles.length === 1
                ? attachTitle
                : `${attachTitle} · ${attachFiles[idx].name.replace(/\.[^.]+$/, '')}`,
            type: attachType,
            version: attachVersion.trim(),
            file_key: urlInfo.file_key,
            file_name: urlInfo.file_name,
            file_size: attachFiles[idx].size,
            content_type: attachFiles[idx].type || 'application/octet-stream',
          }),
        ),
      );

      // 4. Vincular los documentos recién creados al control, fusionando con los
      //    que ya estaban vinculados (dedupe lo hace el backend).
      const newIds = registered.map((r) => r.data.id).filter(Boolean);
      const mergedIds = Array.from(new Set([...ids, ...newIds]));
      await api.put(`/initiative-controls/${control.id}/documents`, { document_ids: mergedIds });

      toast.success(
        attachFiles.length === 1
          ? 'Documento adjuntado y vinculado al control'
          : `${attachFiles.length} documentos adjuntados y vinculados al control`,
      );
      resetAttach();
      setMode('view');
      await onSaved();
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setSaving(false);
      setProgress(0);
    }
  };

  return (
    <>
      {variant === 'attach-button' ? (
        /* Acceso rápido: botón de ícono que abre el modal directo en modo adjuntar */
        <Button
          size="icon"
          variant="ghost"
          className="h-7 w-7 text-muted-foreground hover:text-primary"
          onClick={() => openModal('attach')}
          data-testid={`attach-control-${control.control_id}`}
          aria-label={`Adjuntar documentos al control ${control.name}`}
          title="Adjuntar documentos">
          <HugeiconsIcon icon={Attachment01Icon} className="h-3.5 w-3.5" strokeWidth={1.5} />
        </Button>
      ) : (
        /* Chip compacto en la celda: una sola línea de altura fija */
        <button
          type="button"
          onClick={() => openModal('view')}
          aria-label={`Ver documentos vinculados a ${control.name} (${linked.length})`}
          className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-2.5 py-1 text-xs font-medium text-foreground/80 transition-colors hover:border-primary/40 hover:bg-primary/5 hover:text-foreground">
          <HugeiconsIcon icon={FileAttachmentIcon} className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.5} />
          {linked.length > 0 ? (
            <span>
              {linked.length} {linked.length === 1 ? 'documento' : 'documentos'}
            </span>
          ) : (
            <span className="text-muted-foreground">Sin documentos</span>
          )}
        </button>
      )}

      <Dialog
        open={open}
        onOpenChange={(v) => {
          if (!saving) setOpen(v);
        }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Documentos de {control.name}</DialogTitle>
            <DialogDescription>
              {mode === 'link' &&
                'Seleccione las evidencias que respaldan este control. Vincular un documento no aprueba su contenido ni cambia el estado del control.'}
              {mode === 'attach' &&
                'Suba archivos nuevos. Se registran como evidencia de la iniciativa y se vinculan automáticamente a este control.'}
              {mode === 'view' && 'Evidencias vinculadas a este control. Descárguelas o gestione los vínculos.'}
            </DialogDescription>
          </DialogHeader>

          {mode === 'link' && (
            /* Modo gestión: selección de documentos existentes con checkboxes */
            <div className="max-h-80 space-y-2 overflow-y-auto">
              {available.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  No hay documentos en la iniciativa. Use “Adjuntar documentos” para subir archivos nuevos.
                </p>
              )}
              {available.map((doc) => (
                <label
                  key={doc.id}
                  className="flex items-start gap-3 rounded-lg border border-border p-3 text-sm transition-colors hover:bg-muted/40">
                  <input
                    type="checkbox"
                    className="mt-1 accent-primary"
                    checked={selected.includes(doc.id)}
                    onChange={(e) =>
                      setSelected((prev) => (e.target.checked ? [...prev, doc.id] : prev.filter((id) => id !== doc.id)))
                    }
                  />
                  <span className="min-w-0 break-words">
                    {doc.file_name || doc.title}
                    <span className="block text-xs text-muted-foreground">
                      {doc.title} · {doc.status}
                    </span>
                  </span>
                </label>
              ))}
            </div>
          )}

          {mode === 'attach' && (
            /* Modo adjuntar: subir archivos nuevos */
            <div className="max-h-80 space-y-4 overflow-y-auto pr-1">
              <div className="space-y-1.5">
                <Label htmlFor="control-doc-title" className="text-xs">
                  Título
                </Label>
                <Input
                  id="control-doc-title"
                  value={attachTitle}
                  onChange={(e) => setAttachTitle(e.target.value)}
                  placeholder="Ej: Evidencia de control v1.0"
                  disabled={saving}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="control-doc-version" className="text-xs">
                    Versión documental
                  </Label>
                  <Input
                    id="control-doc-version"
                    value={attachVersion}
                    onChange={(e) => setAttachVersion(e.target.value)}
                    maxLength={40}
                    disabled={saving}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="control-doc-type" className="text-xs">
                    Tipo
                  </Label>
                  <Select value={attachType} onValueChange={setAttachType} disabled={saving}>
                    <SelectTrigger id="control-doc-type" aria-label="Tipo de documento">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DOC_TYPES.map((t) => (
                        <SelectItem key={t} value={t}>
                          {t}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="control-doc-files" className="text-xs">
                  Archivos
                </Label>
                <input
                  id="control-doc-files"
                  type="file"
                  multiple
                  onChange={handleAttachFiles}
                  disabled={saving}
                  className="block w-full text-xs file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-xs file:font-medium file:bg-primary/8 file:text-primary hover:file:bg-primary/12 cursor-pointer"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg,.webp,.txt,.csv"
                  aria-label="Seleccionar archivos para adjuntar"
                />
              </div>
              {attachFiles.length > 0 && (
                <ul className="space-y-1.5">
                  {attachFiles.map((f, idx) => (
                    <li
                      key={`${f.name}-${idx}`}
                      className="flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2 text-sm">
                      <span className="min-w-0 truncate">
                        {f.name}
                        <span className="ml-2 text-xs text-muted-foreground">{formatSize(f.size)}</span>
                      </span>
                      {!saving && (
                        <button
                          type="button"
                          onClick={() => removeAttachFile(idx)}
                          aria-label={`Quitar ${f.name}`}
                          className="shrink-0 text-muted-foreground transition-colors hover:text-foreground">
                          <HugeiconsIcon icon={Cancel01Icon} className="h-4 w-4" strokeWidth={1.5} />
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              {saving && progress > 0 && (
                <div className="space-y-1">
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                    <div className="h-full bg-primary transition-all" style={{ width: `${progress}%` }} />
                  </div>
                  <p className="text-xs text-muted-foreground">Subiendo… {progress}%</p>
                </div>
              )}
            </div>
          )}

          {mode === 'view' && (
            /* Modo visualización: lista de documentos vinculados con descarga */
            <div className="max-h-80 space-y-2 overflow-y-auto">
              {linked.length === 0 ? (
                <div className="rounded-lg border border-dashed border-border p-6 text-center">
                  <HugeiconsIcon
                    icon={FileAttachmentIcon}
                    className="mx-auto h-6 w-6 text-muted-foreground/60"
                    strokeWidth={1.5}
                  />
                  <p className="mt-2 text-sm text-muted-foreground">Este control aún no tiene documentos vinculados.</p>
                </div>
              ) : (
                <ul className="space-y-2">
                  {linked.map((doc) => (
                    <li
                      key={doc.id}
                      className="flex items-center justify-between gap-3 rounded-lg border border-border p-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium">{doc.file_name || doc.title}</p>
                        <p className="text-xs text-muted-foreground">
                          {doc.status} · v{doc.version}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 shrink-0 gap-1.5 text-xs border-border"
                        onClick={() => downloadEvidence(doc)}>
                        <HugeiconsIcon icon={Download01Icon} className="h-3.5 w-3.5" strokeWidth={1.5} />
                        Descargar
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <DialogFooter>
            {mode === 'link' && (
              <>
                <Button variant="outline" disabled={saving} onClick={() => setMode('view')}>
                  Cancelar
                </Button>
                <Button disabled={saving} onClick={save}>
                  {saving ? 'Guardando…' : 'Guardar vínculos'}
                </Button>
              </>
            )}
            {mode === 'attach' && (
              <>
                <Button
                  variant="outline"
                  disabled={saving}
                  onClick={() => {
                    resetAttach();
                    setMode('view');
                  }}>
                  Cancelar
                </Button>
                <Button disabled={saving} onClick={attach}>
                  {saving ? 'Subiendo…' : 'Adjuntar y vincular'}
                </Button>
              </>
            )}
            {mode === 'view' && canEdit && (
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  className="gap-1.5"
                  onClick={() => {
                    setSelected(linked.map((d) => d.id));
                    setMode('link');
                  }}>
                  <HugeiconsIcon icon={Add01Icon} className="h-4 w-4" strokeWidth={1.5} />
                  Vincular documentos
                </Button>
                <Button
                  className="gap-1.5"
                  onClick={() => {
                    resetAttach();
                    setMode('attach');
                  }}>
                  <HugeiconsIcon icon={CloudUploadIcon} className="h-4 w-4" strokeWidth={1.5} />
                  Adjuntar documentos
                </Button>
              </div>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
